import { bind } from '@streaming/mouflon-utils';

import 'navigator.sendbeacon';

import { EPlayerType, TAdditionalFields } from '@customTypes/common';
import { AggregatedMetricsV2 } from '@metrics/AggregatedMetricsV2';
import { hash } from '@metrics/HashObfuscated';
import { TAggregatedFieldsV2 } from '@metrics/IAggregatedMetricsV2';
import { EMetricsEvents } from '@metrics/Metrics.events';
import {
  checkIfPageWasReloaded,
  getPrevSessionStartTime,
  getPrevStreamId,
  getPrevUserId,
  getPrevProjectId,
  setSessionStartTime,
  setStreamId,
  setUserId,
  setProjectId,
} from '@metrics/PageReloadSingleton';
import { TMMPEventBus } from '@utils/EventBus';

import { AggregatedMetrics, TAggregatedFields } from './AggregatedMetrics';
import {
  EReportType,
  IEventDetails,
  IFetchRequestOptions,
  TEvent,
  TEventType,
  TMetricsBodyWithMeta,
  TMetricsCollected,
  TMetricsReport,
} from './IMetrics';
import { MMP_VERSION } from '../version';

const rawToAggregatedMetricsMap: Partial<Record<keyof TMetricsCollected, TAggregatedFields>> = {
  audioJitter: 'AudioJitter',
  videoJitter: 'VideoJitter',
  buffer: 'Buffer',
  framesEncoded: 'FramesEncoded',
  framesSent: 'FramesSent',
  targetBuffer: 'TargetBuffer',
  chunkLoadDuration: 'ChunkLoadDuration',
  chunkLoadTTFB: 'ChunkLoadTTFB',
  playlistLoadDuration: 'PlaylistLoadDuration',
  playlistLoadTTFB: 'PlaylistLoadTTFB',
  chunkSize: 'ChunkSize',
  playlistSize: 'PlaylistSize',
};

const rawToAggregatedMetricsMapV2: Partial<Record<keyof TMetricsCollected, TAggregatedFieldsV2>> = {
  latency: 'Latency',
};

const rawToReportedMetricsMap: Partial<Record<keyof TMetricsCollected, keyof TMetricsReport>> = {
  framesDecoded: 'framesDecoded',
  nackCount: 'nackCount',
  totalFreezesDuration: 'totalFreezesDuration',
  totalPausesDuration: 'totalPausesDuration',
  fecPacketsReceived: 'fecPacketsReceived',
  fecPacketsDiscarded: 'fecPacketsDiscarded',
  framesReceived: 'framesReceived',
  packetsLost: 'packetsLost',
  packetsDiscarded: 'packetsDiscarded',
  packetsReceived: 'packetsReceived',
  totalFramesDecoded: 'totalFramesDecoded',
  totalFramesDropped: 'totalFramesDropped',
  upscaleFactorAvg: 'upscaleFactorAvg',
  upscaleFactorMax: 'upscaleFactorMax',
};

const metricsSendInterval: number = 30000;
const sendTimeout: number = 5000;

export class MetricsCollector {
  private _userId: string | number | null = null;
  private _sessionStartTime: number | null = null;
  private _playerType: EPlayerType | null = null;
  private _streamId: string | null = null;
  private _currentMetricsState: Partial<TMetricsReport> = {};
  private _currentDomain: string = '';
  private _sendMetricsInterval: ReturnType<typeof setInterval> | null = null;
  private _eventsCache: TEvent[] = [];
  private _currentManualResolution: string | null = null;
  private _currentRenditionSwitchMode: 'auto' | 'manual' | null = null;
  private _abTestsMap: Record<string, string> = {};
  private _abTestsSupport: Record<string, boolean> = {};
  private _additionalFields: TAdditionalFields = {};
  private _urlPostfix: string = '';
  private _timeOrigin: number = performance.timeOrigin;
  private _eventSequenceNumber: number = 0;
  private _userType: 'model' | 'user' | 'unknown' = 'unknown';
  private _regularBroadcastStopReason: string = '';
  private _sessionId: string = '';

  private readonly _baseUrl: string;
  private readonly _aggregatedMetrics: AggregatedMetrics = new AggregatedMetrics();
  private readonly _aggregatedMetricsV2: AggregatedMetricsV2 = new AggregatedMetricsV2();
  private _projectId: string;
  private readonly _eventBus: TMMPEventBus;

  constructor(baseUrl: string, projectId: string, eventBus: TMMPEventBus) {
    this._baseUrl = baseUrl;
    this._projectId = projectId;
    this._eventBus = eventBus;

    if (checkIfPageWasReloaded()) {
      this._registerReloadEvent();
    }
  }

  public destroy(): void {
    this.stop();
  }

  public stop(): void {
    this._disarmMetricsSend();
    this.immediatelySendMetrics(true);
    this._sessionStartTime = null;
    this._streamId = null;
    setStreamId('');
    this._eventSequenceNumber = 0;
  }

  public registerMetrics(metrics: TMetricsCollected): void {
    const nonRawMetrics: Array<keyof TMetricsCollected> = Object.keys({
      ...rawToAggregatedMetricsMap,
      ...rawToAggregatedMetricsMapV2,
    }).concat(['totalRoundTripTime', 'responsesReceived']) as Array<keyof TMetricsCollected>;

    if (metrics.totalRoundTripTime !== undefined && metrics.responsesReceived !== undefined) {
      this._aggregatedMetrics.addValue(
        'RTT',
        metrics.totalRoundTripTime / metrics.responsesReceived,
      );

      delete metrics.totalRoundTripTime;
      delete metrics.responsesReceived;
    }

    Object.entries(metrics).forEach(([key, value]: [keyof TMetricsCollected, number]) => {
      if (nonRawMetrics.includes(key)) {
        this._aggregatedMetrics.addValue(
          rawToAggregatedMetricsMap[key] as TAggregatedFields,
          value,
        );
        this._aggregatedMetricsV2.addValue(
          rawToAggregatedMetricsMapV2[key] as TAggregatedFieldsV2,
          value,
        );
      } else {
        const reportKey: keyof TMetricsReport = rawToReportedMetricsMap[
          key
        ] as keyof TMetricsReport;
        this._currentMetricsState[reportKey] = value;
      }
    });
  }

  // Setters without side-effetcs

  public setAbTests(abTests: Record<string, string>): void {
    this._abTestsMap = abTests;
  }

  public setAbTestsSupport(abTestsSupport: Record<string, boolean>): void {
    this._abTestsSupport = abTestsSupport;
  }

  public setAdditionalFields(additionalFields: TAdditionalFields): void {
    this._additionalFields = additionalFields;
  }

  public updateCDNUrl(url: string): void {
    const { host } = new URL(url);
    if (this._currentDomain === host) {
      return;
    }

    this._currentDomain = host;
  }

  public setTimeOrigin(timeOrigin: number): void {
    this._timeOrigin = timeOrigin;
  }

  // Setters with side-effects

  public setUserId(userId: string | number): void {
    if (this._userId === userId) {
      return;
    }
    this._recreateSessionId(userId);
  }

  public setProjectId(projectId: string): void {
    if (this._projectId === projectId) {
      return;
    }
    this._recreateSessionId(undefined, projectId);
  }

  public setUserType(userType: 'model' | 'user'): void {
    this._userType = userType;
  }

  public setPlayerType(playerType: EPlayerType): void {
    if (playerType === this._playerType) {
      return;
    }

    if (this._playerType) {
      this.registerEvent('PlayerTypeSwitched', {
        prevPlayerType: this._playerType,
        nextPlayerType: playerType,
      });
    }

    if (playerType === EPlayerType.BROADCAST) {
      this._urlPostfix = '_broadcast';
    } else if (this._userId) {
      this._urlPostfix = Number(this._userId) % 2 === 0 ? '_user' : '_guest';
    }

    this._playerType = playerType;
    this._recreateSessionId();
  }

  public setStreamId(streamId: string): void {
    if (streamId === this._streamId) {
      return;
    }

    setStreamId(streamId);

    this._streamId = streamId;
    this._recreateSessionId();
  }

  public setManualResolution(nextResolution: string): void {
    if (this._currentManualResolution === nextResolution) {
      return;
    }

    if (this._currentManualResolution) {
      this.registerEvent('RenditionSwitchedManual', {
        prevResolution: this._currentManualResolution,
        nextResolution,
      });
    }

    this._currentManualResolution = nextResolution;
  }

  public setRenditionSwitchMode(nextRenditionSwitchMode: 'manual' | 'auto'): void {
    if (this._currentRenditionSwitchMode === nextRenditionSwitchMode) {
      return;
    }

    if (this._currentRenditionSwitchMode) {
      this.registerEvent('RenditionSelectModeSwitched', {
        prevMode: this._currentRenditionSwitchMode,
        nextMode: nextRenditionSwitchMode,
      });
    }

    this._currentRenditionSwitchMode = nextRenditionSwitchMode;
  }

  public prepareMetrics(): TMetricsReport {
    return {
      ...this._currentMetricsState,
      ...this._aggregatedMetrics.getAllValues(),
      ...this._aggregatedMetricsV2.getAllValues(),
    };
  }

  // Misc public

  public registerEvent(eventType: TEventType, details: IEventDetails = {}): void {
    if (!this.hasEnoughInfoForMetricsAndEvents) {
      return;
    }

    const event: TEvent = this._buildEventsSingleMessage(eventType, details);

    this._registerRawEvent(event);
  }

  public clear(): void {
    this._aggregatedMetrics.clearAllValues();
    this._currentMetricsState = {};
  }

  public setRegularBroadcastStopReason(regularBroadcastStopReason: string): void {
    this._regularBroadcastStopReason = regularBroadcastStopReason;
  }

  public immediatelySendMetrics(isDestroying: boolean = false): void {
    if (!this.hasEnoughInfoForMetricsAndEvents) {
      return;
    }
    const eventType: TEventType = isDestroying ? 'SessionTerminated' : 'TabBlurred';

    this.registerEvent(eventType, { reason: this._regularBroadcastStopReason });

    if (!isDestroying) {
      this._armMetricsSendInterval();
    }
    this._sendCurrentState('all', true);
  }

  private _possiblyCreateSessionId(): void {
    if (this._playerType && this._streamId && !this._sessionStartTime) {
      this._sessionStartTime = performance.now() + this._timeOrigin;
      this._armMetricsSendInterval();
      this.registerEvent('SessionStarted');
      setSessionStartTime(this._sessionStartTime);
      this._sessionId = `${this._sessionStartTime}:${this._userId}`;
      this._eventBus.publish(EMetricsEvents.SESSION_ID_RECREATED, {} as never);
    }
  }

  private _registerRawEvent(event: TEvent): void {
    this._eventsCache.push(event);
    this._eventSequenceNumber++;
  }

  private _registerReloadEvent(): void {
    this._registerRawEvent(
      this._buildEventsSingleMessage(
        'PageReloaded',
        {},
        {
          streamId: getPrevStreamId().toString(),
          userId: getPrevUserId(),
          sessionStartTime: getPrevSessionStartTime(),
          projectId: getPrevProjectId(),
        },
      ),
    );
  }

  private _recreateSessionId(newUserId?: string | number, newProjectId?: string | number): void {
    if (!this._sessionStartTime) {
      if (newUserId !== undefined) {
        this._applyUserId(newUserId);
      }
      if (newProjectId !== undefined) {
        this._applyProjectId(newProjectId);
      }
      this._possiblyCreateSessionId();
      return;
    }
    this.registerEvent('SessionTerminated', { reason: this._regularBroadcastStopReason });

    this._sendCurrentState('metrics');

    this._eventSequenceNumber = 0;
    this._sessionStartTime = performance.now() + this._timeOrigin;
    this._sessionId = `${this._sessionStartTime}:${this._userId}`;

    this._aggregatedMetricsV2.clearReportValues();

    if (newUserId !== undefined) {
      this._applyUserId(newUserId);
    }
    if (newProjectId !== undefined) {
      this._applyProjectId(newProjectId);
    }
    this.registerEvent('SessionStarted');
    this._eventBus.publish(EMetricsEvents.SESSION_ID_RECREATED, {} as never);
  }

  private _armMetricsSendInterval(): void {
    this._disarmMetricsSend();

    this._sendMetricsInterval = setInterval(this._sendCurrentState, metricsSendInterval);
  }

  private _disarmMetricsSend(): void {
    if (!this._sendMetricsInterval) {
      return;
    }
    clearInterval(this._sendMetricsInterval);
    this._sendMetricsInterval = null;
  }

  @bind
  private async _sendCurrentState(
    mode: 'all' | 'events' | 'metrics' = 'all',
    isTerminating: boolean = false,
  ): Promise<void> {
    if (['events', 'all'].includes(mode)) {
      const events: TEvent[] = [...this._eventsCache];
      if (events.length) {
        this._eventsCache = [];
        await this._performFetchRequest({
          url: `${this._baseUrl}/events${this._urlPostfix}`,
          data: { events },
          onError: () => {
            this._eventsCache.push(...events);
          },
          isTerminating,
        });
      }
    }

    if (['metrics', 'all'].includes(mode)) {
      if (!this.hasEnoughInfoForMetricsAndEvents) {
        this.clear();
        return;
      }
      const metrics: TMetricsBodyWithMeta = this._buildSingleMetricsMessage(this.prepareMetrics());
      await this._performFetchRequest({
        url: `${this._baseUrl}/metrics${this._urlPostfix}`,
        data: metrics,
        onSuccess: () => {
          this.clear();
        },
        isTerminating,
      });
    }
  }

  get currentRenditionSelectMode(): 'auto' | 'manual' | null {
    return this._currentRenditionSwitchMode;
  }

  get hasEnoughInfoForMetricsAndEvents(): boolean {
    return !!this._playerType && !!this._streamId && !!this._sessionStartTime && !!this._userId;
  }

  get missingFieldsForMetricsAndEvents(): string[] {
    return Object.entries({
      playerType: this._playerType,
      streamId: this._streamId,
      sessionStartTime: this._sessionStartTime,
      userId: this._userId,
    })
      .filter(([_, value]) => Boolean(value))
      .map(([key]) => key);
  }

  private _buildEventsSingleMessage(
    eventType: TEventType,
    payload: Record<string, any> = {},
    overrides: Partial<TEvent> = {},
  ): TEvent {
    return {
      eventType,
      payload,
      sessionStartTime: this._sessionStartTime!,
      userId: this._userId!,
      streamId: this._streamId!,
      playerImpl: 'MMP',
      playerType: this._playerType!,
      timestamp: Date.now(),
      projectId: this._projectId,
      cdnDomain: this._currentDomain,
      abTests: this._abTestsMap,
      abTestsSupport: this._abTestsSupport,
      mmpVersion: MMP_VERSION,
      sequenceNumber: this._eventSequenceNumber,
      userType: this._userType !== 'unknown' ? this._userType : undefined,
      ...(this._additionalFields || {}),
      ...overrides,
    };
  }

  private _buildSingleMetricsMessage(
    metrics: TMetricsReport,
    overrides: Partial<TMetricsBodyWithMeta> = {},
  ): TMetricsBodyWithMeta {
    return {
      ...metrics,
      sessionStartTime: this._sessionStartTime!,
      userId: this._userId!,
      streamId: this._streamId!,
      playerImpl: 'MMP',
      playerType: this._playerType!,
      timestamp: Date.now(),
      projectId: this._projectId,
      cdnDomain: this._currentDomain,
      reportType: EReportType.PRECLEAR,
      abTests: this._abTestsMap,
      abTestsSupport: this._abTestsSupport,
      mmpVersion: MMP_VERSION,
      userType: this._userType !== 'unknown' ? this._userType : undefined,
      ...(this._additionalFields || {}),
      ...overrides,
    };
  }

  private async _performFetchRequest({
    url,
    data,
    onError = () => {},
    onSuccess = () => {},
    isTerminating = false,
  }: IFetchRequestOptions): Promise<void> {
    const serializedData: string = JSON.stringify(data);
    const abortController: AbortController = new AbortController();
    const abortTimeout: ReturnType<typeof setTimeout> = setTimeout(() => {
      abortController.abort('Timeout');
    }, sendTimeout);

    const body: string = `${hash(serializedData)}${serializedData}`;

    if (isTerminating) {
      navigator.sendBeacon(url, body);
    } else {
      try {
        const response: Response = await fetch(url, {
          body,
          signal: abortController.signal,
          method: 'POST',
          cache: 'no-cache',
        });

        if (!response.ok) {
          this._onFetchError({
            payloadSize: body.length,
            responseCode: response.status,
            responseBody: await response.text(),
            requestUrl: url,
            nativeError: '',
          });
        }
        clearTimeout(abortTimeout);
        onSuccess();
      } catch (e) {
        this._onFetchError({
          payloadSize: body.length,
          responseCode: 0,
          responseBody: 'N/A',
          requestUrl: url,
          nativeError: e.toString(),
        });
        onError(e);
      }
    }
  }

  private async _onFetchError(error: Record<string, string | number>): Promise<void> {
    try {
      await fetch(`${this._baseUrl}/logs`, {
        body: JSON.stringify({
          ...error,
          userId: this._userId ?? -1,
          sessionStartTime: this._sessionStartTime ?? -1,
        }),
        method: 'POST',
        cache: 'no-cache',
      });
    } catch (e) {
      console.warn('Could not report fetch error:', e);
    }
  }

  get baseUrl(): string {
    return this._baseUrl;
  }

  get projectId(): string {
    return this._projectId;
  }

  get sessionId(): string | undefined {
    return this._sessionId;
  }

  private _applyUserId(userId: string | number): void {
    if (this._playerType && this._playerType === EPlayerType.BROADCAST) {
      this._urlPostfix = '_broadcast';
    } else {
      this._urlPostfix = Number(userId) % 2 === 0 ? '_user' : '_guest';
    }

    this._userId = userId;
    setUserId(userId);
  }

  private _applyProjectId(projectId: string): void {
    this._projectId = projectId;
    setProjectId(projectId);
  }
}
